/**
 * Glyph normalization — make ANY character a PowerPoint deck can carry render
 * with the fonts we actually ship, instead of tofu (□).
 *
 * Two systematic sources of "tofu" exist in real decks:
 *
 *  1. Mathematical Alphanumeric Symbols (U+1D400–U+1D7FF). PowerPoint's equation
 *     editor and the "math" font styling emit these — e.g. the bold phi 𝛗
 *     (U+1D6D7) used all over chemistry/physics decks. Essentially no normal text
 *     font (Calibri/Carlito/Arial/Noto Sans) carries this block, so every one of
 *     them renders as a blank box. They are, however, just STYLED duplicates of
 *     plain ASCII / Greek / digits, so we can fold each one back to its base
 *     letter (𝛗 → φ, 𝐀 → A, 𝟑 → 3) which the bundled fonts DO cover. This is a
 *     complete, algorithmic mapping over the whole block — not a hand-picked list.
 *
 *  2. Dingbats / decorative symbols a free text font lacks (e.g. the heavy arrow
 *     ➪ U+27AA). These have no algorithmic base form, so a small curated table
 *     maps each to the closest glyph the bundled fonts DO render (➪ → →).
 *
 * Applied uniformly to every run's text and every field's text at parse time, so
 * measurement, wrapping and drawing all operate on renderable characters.
 */

// Base sequences, in the EXACT codepoint order of each Mathematical Alphanumeric
// sub-block, so `start + offset` folds to `seq[offset]`.
const LATIN = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"; // 52
const DIGITS = "0123456789"; // 10
// Greek math blocks are 58 cps: 25 uppercase (Α–Ρ, ϴ, Σ–Ω) · ∇ · 25 lowercase
// (α–ω incl. final sigma) · 7 variant symbols (∂ ϵ ϑ ϰ ϕ ϱ ϖ). Folded to plain
// Greek where one exists (ϴ→Θ, ϕ→φ, …); ∇/∂ kept (the bundled fonts cover them).
const GREEK =
  "ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΘΣΤΥΦΧΨΩ" + // 25 upper (ϴ→Θ)
  "∇" + // ∇
  "αβγδεζηθικλμνξοπρςστυφχψω" + // 25 lower (incl. ς)
  "∂εθκφρπ"; // ∂ ϵ→ε ϑ→θ ϰ→κ ϕ→φ ϱ→ρ ϖ→π

// The Mathematical Alphanumeric codepoint ENCODES a style (bold/italic/script/…),
// not just a letter. Folding to a base letter would drop that weight/slant — so
// each block also carries the bold/italic it implies, which we OR into the run's
// own style (e.g. 𝛗 is "Mathematical Bold" phi → render φ in bold). Script /
// fraktur / double-struck / monospace have no plain-font equivalent, so they fold
// to the base letter with no style delta.
const B = { b: true }, I = { i: true }, BI = { b: true, i: true }, NONE = {};
type Style = { b?: boolean; i?: boolean };

// Start codepoint → [base sequence, implied style], for every assigned sub-block
// of U+1D400–U+1D7FF.
const BLOCKS: Array<[number, string, Style]> = [
  // Latin: 13 styles × (26 upper + 26 lower), contiguous (reserved holes — italic
  // h, the script/fraktur/double-struck letters unified into Letterlike Symbols —
  // keep their slot, so the offset of every other letter stays correct).
  [0x1d400, LATIN, B], [0x1d434, LATIN, I], [0x1d468, LATIN, BI], [0x1d49c, LATIN, NONE], // bold, italic, bold-italic, script
  [0x1d4d0, LATIN, B], [0x1d504, LATIN, NONE], [0x1d538, LATIN, NONE], [0x1d56c, LATIN, B], // bold-script, fraktur, double-struck, bold-fraktur
  [0x1d5a0, LATIN, NONE], [0x1d5d4, LATIN, B], [0x1d608, LATIN, I], [0x1d63c, LATIN, BI], // sans, sans-bold, sans-italic, sans-bold-italic
  [0x1d670, LATIN, NONE], // monospace
  // Greek: bold, italic, bold-italic, sans-bold, sans-bold-italic × 58.
  [0x1d6a8, GREEK, B], [0x1d6e2, GREEK, I], [0x1d71c, GREEK, BI], [0x1d756, GREEK, B], [0x1d790, GREEK, BI],
  // Digits: bold, double-struck, sans, sans-bold, monospace × 10.
  [0x1d7ce, DIGITS, B], [0x1d7d8, DIGITS, NONE], [0x1d7e2, DIGITS, NONE], [0x1d7ec, DIGITS, B], [0x1d7f6, DIGITS, NONE],
];

/** Math letters Unicode encodes in the Letterlike Symbols block (the "holes" in
 *  the styled Latin blocks) → [base letter, implied style]. */
const LETTERLIKE: Record<number, [string, Style]> = {
  0x210e: ["h", I], // ℎ italic h
  0x212c: ["B", NONE], 0x2130: ["E", NONE], 0x2131: ["F", NONE], 0x210b: ["H", NONE], 0x2110: ["I", NONE], 0x2112: ["L", NONE],
  0x2133: ["M", NONE], 0x211b: ["R", NONE], 0x212f: ["e", NONE], 0x210a: ["g", NONE], 0x2134: ["o", NONE], // script
  0x212d: ["C", NONE], 0x210c: ["H", NONE], 0x2111: ["I", NONE], 0x211c: ["R", NONE], 0x2128: ["Z", NONE], // fraktur
  0x2102: ["C", NONE], 0x210d: ["H", NONE], 0x2115: ["N", NONE], 0x2119: ["P", NONE], 0x211a: ["Q", NONE], 0x211d: ["R", NONE], 0x2124: ["Z", NONE], // double-struck
};

// codepoint → [base char, implied style], built once from the blocks + holes.
const MATH = new Map<number, [string, Style]>();
for (const [start, seq, style] of BLOCKS) {
  for (let i = 0; i < seq.length; i++) MATH.set(start + i, [seq[i], style]);
}
for (const [cp, v] of Object.entries(LETTERLIKE)) MATH.set(Number(cp), v);

/** Decorative symbols/dingbats the bundled text fonts don't carry → the closest
 *  glyph they DO (so a heavy arrow shows an arrow, not a blank box). Extend as
 *  new decks surface more. Targets here are all verified present in Carlito. */
const SYMBOL_FALLBACK: Record<number, string> = {
  // heavy / decorative RIGHT arrows → "→"
  0x27a1: "→", 0x27a4: "→", 0x27a8: "→", 0x27a9: "→",
  0x27aa: "→", 0x27ab: "→", 0x2794: "→", 0x2799: "→",
  0x279c: "→", 0x279d: "→", 0x279e: "→", 0x279f: "→",
  0x21e8: "→", 0x21d2: "→", 0x2b95: "→", 0x2b62: "→",
  // heavy LEFT arrows → "←"
  0x2b05: "←", 0x21e6: "←", 0x21d0: "←", 0x2b60: "←",
  // up / down → "↑" / "↓"
  0x2b06: "↑", 0x21e7: "↑", 0x2b07: "↓", 0x21e9: "↓",
};

/** Cheap guard: a string needs work only if it carries a codepoint one of our
 *  fold tables could touch — so plain ASCII/Latin/Greek text returns fast.
 *  Two UTF-16 ranges, checked per code UNIT (charCodeAt, not codePointAt):
 *   - c ≥ 0xD800: a high/low surrogate, i.e. ANY supplementary-plane char — this
 *     covers all of Mathematical Alphanumeric Symbols (U+1D400–U+1D7FF, the math
 *     block) which is encoded as surrogate pairs in UTF-16.
 *   - 0x2100 ≤ c ≤ 0x2B95: the BMP span holding Letterlike Symbols (the math
 *     "holes", from 0x2102) and the decorative/arrow symbols in SYMBOL_FALLBACK
 *     (up to 0x2B95). A broad superset of those exact codepoints — a false
 *     positive just means normalize() runs and passes the char through unchanged. */
function needsNormalize(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c >= 0xd800 || (c >= 0x2100 && c <= 0x2b95)) return true;
  }
  return false;
}

/**
 * Fold every character to one the bundled fonts can render: Mathematical
 * Alphanumeric Symbols → their base ASCII/Greek/digit, then decorative symbols →
 * a covered stand-in. Anything already renderable passes through untouched.
 */
export function normalizeGlyphs(text: string): string {
  if (!text || !needsNormalize(text)) return text;
  let out = "";
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    out += MATH.get(cp)?.[0] ?? SYMBOL_FALLBACK[cp] ?? ch;
  }
  return out;
}

export interface StyledSegment { text: string; bold: boolean; italic: boolean; }

/**
 * Like {@link normalizeGlyphs}, but split into segments carrying the bold/italic
 * the source codepoints IMPLY (Mathematical Bold/Italic letters fold to a plain
 * letter PLUS their weight/slant). Consecutive same-style characters merge, so a
 * run with no math styling returns a single segment with no delta. Callers OR
 * each segment's bold/italic onto the run's own style.
 */
export function normalizeStyled(text: string): StyledSegment[] {
  if (!text || !needsNormalize(text)) return [{ text, bold: false, italic: false }];
  const segs: StyledSegment[] = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    const m = MATH.get(cp);
    const out = m?.[0] ?? SYMBOL_FALLBACK[cp] ?? ch;
    const bold = !!m?.[1].b, italic = !!m?.[1].i;
    const last = segs[segs.length - 1];
    if (last && last.bold === bold && last.italic === italic) last.text += out;
    else segs.push({ text: out, bold, italic });
  }
  return segs;
}
